import React, { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import _ from 'lodash';
import moment from 'moment-timezone';
import './style.scss';
import MenuItem from '@material-ui/core/MenuItem';
import TextareaAutosize from '@material-ui/core/TextareaAutosize';
import DefaultTextField from 'components/Utils/DefaultTextField';
import DefaultAutoComplete from 'components/Utils/DefaultAutoComplete';
import TimeIntervalField from 'components/Utils/TimeIntervalField';
import DefaultButton from 'components/Utils/NewDefaultButton';
import { DefaultSwitch } from 'services/UIUtils';
import AppointmentUtils from 'services/AppointmentUtils';
import DefaultSelectField from 'components/Utils/DefaultSelectField';
import Icon from 'components/Utils/NewIcon';
import PerformanceUtils from 'services/PerformanceUtils';
import { v4 as uuidV4 } from 'uuid';
import StackItemsView from 'components/Common/StackItemsView';
import { useAppDispatch } from 'careand-redux/useAppDispatch';
import { useAppSelector } from 'careand-redux/store';
import { api } from 'careand-redux/api/ApiThunks';
import PhysicianUtils from 'services/PhysicianUtils';
import { PhysicianPhysicianTypeEnum } from 'generated/api';
import ConcernRequirementModal from 'components/Clinic/ConcernRequirementModal';
import DefaultTooltip from 'components/Utils/DefaultTooltip';
import {
  ConcernRequirementSelector,
  ConcernRequirementBadge,
} from 'components/ConcernRequirements';
import { Tree, Dropdown } from 'antd';
import ResponsiveColumns from 'components/ResponsiveColumns';

const RichTextEditable = lazy(() => import('components/Utils/RichTextEditable'));

const SUPERTYPE_ADD_NEW = 'Create New';
const REQUIREMENT_ADD_NEW = '__add_new__';

const normalizeConcern = (concern) => {
  const normalized = { ...concern };
  normalized.delivery_options = normalized.delivery_options || [];
  normalized.minutes = parseInt(normalized.minutes) || 0;
  normalized.capacity_duration_minutes = normalized.capacity_duration_minutes
    ? parseInt(normalized.capacity_duration_minutes)
    : undefined;
  normalized.max_simultaneous_appointments = normalized.max_simultaneous_appointments
    ? parseInt(normalized.max_simultaneous_appointments)
    : undefined;
  if (!normalized.booking_resource_id) delete normalized.booking_resource_id;
  delete normalized.appointment_data;
  return normalized;
};

const normalizeOption = (option) => ({
  ...option,
  concerns: (option.concerns || []).map(normalizeConcern),
});

const TreeNodeTitle = ({ icon, label, actions }) => (
  <span className="tree-node-title-wrapper">
    {icon && <Icon src={icon} width="18px" height="18px" />}
    <span className="tree-node-label">{label}</span>
    {actions && actions.length > 0 && (
      <Dropdown
        menu={{
          items: actions.map((a) => ({ key: a.key, label: a.label })),
          onClick: ({ key, domEvent }) => {
            domEvent.stopPropagation();
            actions.find((a) => a.key === key)?.onClick();
          },
        }}
        trigger={['click']}
      >
        <span className="tree-node-ellipsis" onClick={(e) => e.stopPropagation()}>
          ⋮
        </span>
      </Dropdown>
    )}
  </span>
);

const BookingFlowOptions = () => {
  const dispatch = useAppDispatch();
  const options = useAppSelector((state) => state.appointments.options);
  const [selectedOption, setSelectedOption] = useState();
  const [selectedConcern, setSelectedConcern] = useState();
  const [selectedKeys, setSelectedKeys] = useState([]);
  const [plans, setPlans] = useState([]);
  const [localOptions, setLocalOptions] = useState([]);
  const [saving, setSaving] = useState(false);
  const navigateTo = useRef();

  const navigateToColumn = useCallback((idx) => {
    setTimeout(() => navigateTo.current?.(idx));
  }, []);

  useEffect(() => {
    dispatch(api.Appointments.getBookingFlowOptions({ all: true }));
    dispatch(api.Appointments.getConcernRequirements());
    dispatch(api.Templates.getDocumentTemplates());
    dispatch(api.Payments.getAllPaymentPlans()).then((plans) => {
      if (plans !== undefined) {
        setPlans(plans);
      }
    });
  }, [dispatch]);

  // Sync local options from Redux (normalize so comparisons are stable)
  useEffect(() => {
    setLocalOptions(PerformanceUtils.clone(options).map(normalizeOption));
  }, [options]);

  const orderedLocalOptions = useMemo(
    () => PerformanceUtils.sortBy(localOptions, ['order'], false),
    [localOptions],
  );

  const localOptionsById = useMemo(() => {
    const map = {};
    localOptions.forEach((o) => {
      map[o.id] = o;
    });
    return map;
  }, [localOptions]);

  const orderedPlans = useMemo(
    () => PerformanceUtils.sortBy(plans, ['payment_plan.active', 'payment_plan.name'], false),
    [plans],
  );

  const normalizedOptions = useMemo(() => options.map(normalizeOption), [options]);
  const [hasChanges, setHasChanges] = useState(false);
  useEffect(() => {
    const timer = setTimeout(() => {
      setHasChanges(!_.isEqual(localOptions, normalizedOptions));
    }, 500);
    return () => clearTimeout(timer);
  }, [localOptions, normalizedOptions]);

  const handleCreateOptionBelow = useCallback(
    (childOf, afterOrder) => {
      const newOrder = (parseInt(afterOrder) || 0) + 1;
      const newOption = normalizeOption({
        id: uuidV4(),
        title: '',
        text: '',
        child_of: childOf || undefined,
        order: newOrder,
        concerns: [],
      });
      setLocalOptions((prev) => [
        ...prev.map((o) => {
          // Increment order of siblings at or after the new position
          if (
            (o.child_of || undefined) === (childOf || undefined) &&
            (parseInt(o.order) || 0) >= newOrder
          ) {
            return { ...o, order: (parseInt(o.order) || 0) + 1 };
          }
          return o;
        }),
        newOption,
      ]);
      setSelectedOption(newOption);
      setSelectedConcern(undefined);
      setSelectedKeys([`option::${newOption.id}`]);
      navigateToColumn(1);
    },
    [navigateToColumn],
  );

  const handleCreateConcernBelow = useCallback(
    (parentOptionId) => {
      const parentOption = localOptions.find((o) => o.id === parentOptionId);
      if (!parentOption) return;
      const newConcern = normalizeConcern({ id: uuidV4() });
      setLocalOptions((prev) =>
        prev.map((o) => {
          if (o.id !== parentOptionId) return o;
          return { ...o, concerns: [...(o.concerns || []), newConcern] };
        }),
      );
      setSelectedOption(parentOption);
      setSelectedConcern(newConcern);
      setSelectedKeys([`concern::${parentOptionId}::${newConcern.id}`]);
      navigateToColumn(1);
    },
    [localOptions, navigateToColumn],
  );

  const treeData = useMemo(() => {
    // Build a map of option id -> tree node for nesting via child_of
    const optionById = {};
    orderedLocalOptions.forEach((option) => {
      optionById[option.id] = option;
    });

    const isRoot = (option) => !option.child_of || !optionById[option.child_of];

    const buildOptionActions = (option) => {
      const actions = [
        {
          key: 'create-option',
          label: 'Create Option Below',
          onClick: () => handleCreateOptionBelow(option.child_of, option.order),
        },
        {
          key: 'create-child-option',
          label: 'Create Child Option',
          onClick: () => handleCreateOptionBelow(option.id, 0),
        },
        {
          key: 'create-child-concern',
          label: 'Create Child Concern',
          onClick: () => handleCreateConcernBelow(option.id),
        },
      ];
      if (!isRoot(option)) {
        actions.push({
          key: 'create-concern',
          label: 'Create Concern Below',
          onClick: () => handleCreateConcernBelow(option.child_of),
        });
      }
      return actions;
    };

    const buildConcernActions = (parentOptionId) => [
      {
        key: 'create-option',
        label: 'Create Option Below',
        onClick: () => {
          const siblings = orderedLocalOptions.filter((o) => o.child_of === parentOptionId);
          const maxOrder = siblings.reduce((max, o) => Math.max(max, parseInt(o.order || '0')), 0);
          handleCreateOptionBelow(parentOptionId, maxOrder);
        },
      },
      {
        key: 'create-concern',
        label: 'Create Concern Below',
        onClick: () => handleCreateConcernBelow(parentOptionId),
      },
    ];

    const mapConcernNode = (option, concern, actions) => ({
      key: `concern::${option.id}::${concern.id}`,
      title: <TreeNodeTitle label={concern.text || 'Untitled Concern'} actions={actions} />,
      isLeaf: true,
    });

    const mapOption = (option) => ({
      key: `option::${option.id}`,
      title: (
        <TreeNodeTitle
          icon={option.icon || 'folder'}
          label={option.title || 'Untitled Type'}
          actions={buildOptionActions(option)}
        />
      ),
      children: [], // filled in below
    });

    // Create tree nodes for all options
    const nodeMap = {};
    orderedLocalOptions.forEach((option) => {
      nodeMap[option.id] = mapOption(option);
    });

    // Nest only ENABLED child options under their parent
    const rootIds = [];
    orderedLocalOptions.forEach((option) => {
      if (option.child_of && nodeMap[option.child_of]) {
        if (!option.disabled) {
          nodeMap[option.child_of].children.push(nodeMap[option.id]);
        }
      } else {
        rootIds.push(option.id);
      }
    });

    // Append ENABLED concern leaves, then a "Disabled" folder for each option
    orderedLocalOptions.forEach((option) => {
      const enabledConcerns = (option.concerns || []).filter(({ disabled }) => !disabled);
      const disabledConcerns = (option.concerns || []).filter(({ disabled }) => !!disabled);
      const disabledChildOptions = orderedLocalOptions.filter(
        (o) => o.child_of === option.id && o.disabled,
      );
      const actions = buildConcernActions(option.id);

      // Add enabled concerns
      enabledConcerns.forEach((concern) => {
        nodeMap[option.id].children.push(mapConcernNode(option, concern, actions));
      });

      // Add "Disabled" folder if there are any disabled children
      if (disabledChildOptions.length > 0 || disabledConcerns.length > 0) {
        const disabledChildren = [
          ...disabledChildOptions.map((o) => ({
            ...nodeMap[o.id],
            className: 'tree-node-disabled',
          })),
          ...disabledConcerns.map((concern) => ({
            ...mapConcernNode(option, concern, actions),
            className: 'tree-node-disabled',
          })),
        ];
        nodeMap[option.id].children.push({
          key: `__disabled::${option.id}`,
          title: 'Disabled',
          selectable: false,
          className: 'tree-node-disabled-folder',
          children: disabledChildren,
        });
      }
    });

    const enabledRoots = rootIds.filter((id) => !optionById[id].disabled);
    const disabledRoots = rootIds.filter((id) => !!optionById[id].disabled);

    const nodes = enabledRoots.map((id) => nodeMap[id]);
    if (disabledRoots.length > 0) {
      nodes.push({
        key: '__disabled_group__',
        title: 'Disabled Types',
        selectable: false,
        className: 'tree-node-disabled-folder',
        children: disabledRoots.map((id) => ({
          ...nodeMap[id],
          className: 'tree-node-disabled',
        })),
      });
    }
    return nodes;
  }, [orderedLocalOptions, handleCreateOptionBelow, handleCreateConcernBelow]);

  const onTreeSelect = useCallback(
    (keys) => {
      if (!keys.length) {
        setSelectedOption(undefined);
        setSelectedConcern(undefined);
        setSelectedKeys([]);
        return;
      }
      const key = keys[0];
      setSelectedKeys([key]);

      if (key.startsWith('option::')) {
        const optionId = key.slice('option::'.length);
        const option = localOptions.find((o) => o.id === optionId);
        setSelectedOption(option);
        setSelectedConcern(undefined);
        navigateToColumn(1);
      } else if (key.startsWith('concern::')) {
        const rest = key.slice('concern::'.length);
        const sep = rest.indexOf('::');
        const optionId = rest.slice(0, sep);
        const concernId = rest.slice(sep + 2);
        const option = localOptions.find((o) => o.id === optionId);
        const concern = (option?.concerns || []).find((c) => c.id === concernId);
        setSelectedOption(option);
        setSelectedConcern(concern);
        navigateToColumn(1);
      }
    },
    [localOptions, navigateToColumn],
  );

  const onConcernChange = useCallback(
    (updatedConcern) => {
      if (!selectedOption) return;
      setLocalOptions((prev) =>
        prev.map((o) => {
          if (o.id !== selectedOption.id) return o;
          let inserted = false;
          let newConcerns = (o.concerns || []).map((c) => {
            if (c.id === updatedConcern.id) {
              inserted = true;
              return updatedConcern;
            }
            return c;
          });
          if (!inserted) {
            newConcerns = [...newConcerns, updatedConcern];
          }
          return { ...o, concerns: newConcerns };
        }),
      );
      setSelectedConcern(updatedConcern);
    },
    [selectedOption],
  );

  // --- Drag & drop ---

  const isDisabledKey = useCallback(
    (key) => {
      if (key === '__disabled_group__') return true;
      if (key.startsWith('option::')) {
        const id = key.slice('option::'.length);
        return !!localOptionsById[id]?.disabled;
      }
      if (key.startsWith('concern::')) {
        const rest = key.slice('concern::'.length);
        const optId = rest.slice(0, rest.indexOf('::'));
        return !!localOptionsById[optId]?.disabled;
      }
      return false;
    },
    [localOptionsById],
  );

  const isRootOptionKey = useCallback(
    (key) => {
      if (!key.startsWith('option::')) return false;
      const id = key.slice('option::'.length);
      const opt = localOptionsById[id];
      return !opt?.child_of || !localOptionsById[opt.child_of];
    },
    [localOptionsById],
  );

  const isDescendantOf = useCallback(
    (ancestorId, nodeId) => {
      let current = localOptionsById[nodeId];
      const visited = new Set();
      while (current?.child_of) {
        if (visited.has(current.id)) return false;
        visited.add(current.id);
        if (current.child_of === ancestorId) return true;
        current = localOptionsById[current.child_of];
      }
      return false;
    },
    [localOptionsById],
  );

  const isDisabledFolder = useCallback((key) => {
    return key === '__disabled_group__' || key.startsWith('__disabled::');
  }, []);

  const allowDrop = useCallback(
    ({ dropNode, dragNode, dropPosition }) => {
      const dropKey = dropNode.key;
      const dragKey = dragNode.key;

      // Allow dropping INTO a disabled folder (position 0)
      if (isDisabledFolder(dropKey)) {
        // Only allow drop into (not gap around)
        if (dropPosition !== 0) return false;
        // Concerns can't go into root disabled folder (no parent option)
        if (dropKey === '__disabled_group__' && dragKey.startsWith('concern::')) return false;
        return true;
      }

      // Can't drop at gap around disabled items
      if (isDisabledKey(dropKey) && dropPosition !== 0) return false;

      // Concerns can't be at root level (gap next to a root option)
      if (dragKey.startsWith('concern::') && dropPosition !== 0) {
        if (isRootOptionKey(dropKey)) return false;
      }

      // Prevent option cycles
      if (dragKey.startsWith('option::') && dropKey.startsWith('option::') && dropPosition === 0) {
        const dragId = dragKey.slice('option::'.length);
        const dropId = dropKey.slice('option::'.length);
        if (dragId === dropId || isDescendantOf(dragId, dropId)) return false;
      }

      return true;
    },
    [isDisabledKey, isDisabledFolder, isRootOptionKey, isDescendantOf],
  );

  const onDrop = useCallback(
    (info) => {
      const dragKey = info.dragNode.key;
      const dropKey = info.node.key;
      const dropToGap = info.dropToGap;

      // Compute relative position: -1 above, 0 into, 1 below
      const dropPos = info.node.pos.split('-');
      const relPos = info.dropPosition - Number(dropPos[dropPos.length - 1]);

      // Can't drag disabled folders themselves
      if (isDisabledFolder(dragKey)) return;

      const parseOptionKey = (k) => k.slice('option::'.length);
      const parseConcernKey = (k) => {
        const rest = k.slice('concern::'.length);
        const sep = rest.indexOf('::');
        return { optionId: rest.slice(0, sep), concernId: rest.slice(sep + 2) };
      };

      // Handle drop into a disabled folder
      if (isDisabledFolder(dropKey) && !dropToGap) {
        setLocalOptions((prev) => {
          const next = JSON.parse(JSON.stringify(prev));
          const byId = {};
          next.forEach((o) => {
            byId[o.id] = o;
          });

          if (dropKey === '__disabled_group__' && dragKey.startsWith('option::')) {
            // Drop into root "Disabled Types" → mark disabled, remove child_of
            const dragId = parseOptionKey(dragKey);
            const dragOpt = byId[dragId];
            if (!dragOpt) return prev;
            dragOpt.disabled = true;
            dragOpt.child_of = undefined;
          } else if (dropKey.startsWith('__disabled::')) {
            // Drop into an option's "Disabled" folder
            const parentOptionId = dropKey.slice('__disabled::'.length);
            const parentOpt = byId[parentOptionId];
            if (!parentOpt) return prev;

            if (dragKey.startsWith('option::')) {
              // Option → mark disabled, set child_of to parent
              const dragId = parseOptionKey(dragKey);
              const dragOpt = byId[dragId];
              if (!dragOpt) return prev;
              dragOpt.disabled = true;
              dragOpt.child_of = parentOptionId;
            } else if (dragKey.startsWith('concern::')) {
              // Concern → remove from source, add to parent's concerns as disabled
              const { optionId: srcOptId, concernId: dragConcernId } = parseConcernKey(dragKey);
              const srcOpt = byId[srcOptId];
              if (!srcOpt) return prev;
              const cIdx = (srcOpt.concerns || []).findIndex((c) => c.id === dragConcernId);
              if (cIdx === -1) return prev;
              const [concern] = srcOpt.concerns.splice(cIdx, 1);
              concern.disabled = true;
              parentOpt.concerns = [...(parentOpt.concerns || []), concern];
            }
          }

          return next;
        });
        return;
      }

      setLocalOptions((prev) => {
        const next = JSON.parse(JSON.stringify(prev));
        const byId = {};
        next.forEach((o) => {
          byId[o.id] = o;
        });

        if (dragKey.startsWith('option::')) {
          const dragId = parseOptionKey(dragKey);
          const dragOpt = byId[dragId];
          if (!dragOpt) return prev;

          if (!dropToGap && dropKey.startsWith('option::')) {
            // Drop INTO an option → becomes child of that option
            const targetId = parseOptionKey(dropKey);
            if (dragId === targetId || isDescendantOf(dragId, targetId)) return prev;
            dragOpt.child_of = targetId;
            dragOpt.disabled = false;
            const siblings = next.filter((o) => o.child_of === targetId && o.id !== dragId);
            dragOpt.order = siblings.length;
          } else {
            // Drop at gap → becomes sibling
            let newParentId;
            let refId;

            if (dropKey.startsWith('option::')) {
              const targetId = parseOptionKey(dropKey);
              newParentId = byId[targetId]?.child_of;
              refId = targetId;
            } else if (dropKey.startsWith('concern::')) {
              const { optionId } = parseConcernKey(dropKey);
              newParentId = optionId;
            }

            dragOpt.child_of = newParentId || undefined;
            dragOpt.disabled = false;

            // Reorder siblings
            const siblings = next
              .filter(
                (o) => (o.child_of || undefined) === (newParentId || undefined) && o.id !== dragId,
              )
              .sort((a, b) => parseInt(a.order || '0') - parseInt(b.order || '0'));

            let insertIndex = siblings.length;
            if (refId) {
              const refIdx = siblings.findIndex((o) => o.id === refId);
              if (refIdx !== -1) {
                insertIndex = relPos <= 0 ? refIdx : refIdx + 1;
              }
            }

            siblings.splice(Math.max(0, insertIndex), 0, dragOpt);
            siblings.forEach((sib, i) => {
              sib.order = i;
            });
          }
        } else if (dragKey.startsWith('concern::')) {
          const { optionId: srcOptId, concernId: dragConcernId } = parseConcernKey(dragKey);
          const srcOpt = byId[srcOptId];
          if (!srcOpt) return prev;

          const cIdx = (srcOpt.concerns || []).findIndex((c) => c.id === dragConcernId);
          if (cIdx === -1) return prev;
          const [concern] = srcOpt.concerns.splice(cIdx, 1);
          concern.disabled = false;

          if (!dropToGap && dropKey.startsWith('option::')) {
            // Drop INTO an option → append to its concerns
            const targetId = parseOptionKey(dropKey);
            const targetOpt = byId[targetId];
            if (!targetOpt) return prev;
            targetOpt.concerns = [...(targetOpt.concerns || []), concern];
          } else if (dropKey.startsWith('concern::')) {
            // Drop at gap next to a concern → insert near it
            const { optionId: tgtOptId, concernId: tgtConcernId } = parseConcernKey(dropKey);
            const targetOpt = byId[tgtOptId];
            if (!targetOpt) return prev;
            const concerns = [...(targetOpt.concerns || [])];
            const tgtIdx = concerns.findIndex((c) => c.id === tgtConcernId);
            const insertAt = relPos <= 0 ? tgtIdx : tgtIdx + 1;
            concerns.splice(Math.max(0, insertAt), 0, concern);
            targetOpt.concerns = concerns;
          } else if (dropKey.startsWith('option::')) {
            // Drop at gap next to a child option → add to that option's parent
            const targetId = parseOptionKey(dropKey);
            const targetOpt = byId[targetId];
            const parentId = targetOpt?.child_of;
            if (parentId && byId[parentId]) {
              byId[parentId].concerns = [...(byId[parentId].concerns || []), concern];
            } else {
              // Root level – put it back (shouldn't happen with allowDrop)
              srcOpt.concerns.splice(cIdx, 0, concern);
            }
          }
        }

        return next;
      });
    },
    [isDisabledKey, isDisabledFolder, isDescendantOf],
  );

  const handleSaveAll = useCallback(async () => {
    setSaving(true);
    const originalById = {};
    normalizedOptions.forEach((o) => {
      originalById[o.id] = o;
    });

    const changed = localOptions.filter((o) => !_.isEqual(o, originalById[o.id]));

    await Promise.all(
      changed.map((option) =>
        dispatch(
          api.Appointments.addBookingFlowOption({
            bookingFlowOption: { ...option, order: parseInt(option.order || '0') },
          }),
          { errorTitle: 'Save Booking Flow Options' },
        ),
      ),
    );
    setSaving(false);
  }, [localOptions, normalizedOptions, dispatch]);

  const onOptionChange = useCallback((updatedOption) => {
    setLocalOptions((prev) => prev.map((o) => (o.id === updatedOption.id ? updatedOption : o)));
    setSelectedOption(updatedOption);
  }, []);

  return (
    <div className="booking-flow-options full-height full-width">
      <ResponsiveColumns
        navigateTo={navigateTo}
        columns={[
          {
            name: 'Tree',
            element: (
              <div className="booking-flow-tree-panel">
                <div className="tree-actions">
                  <DefaultButton
                    name={saving ? 'Saving...' : 'Save All Changes'}
                    className={hasChanges ? 'blue' : ''}
                    disabled={!hasChanges || saving}
                    onClick={handleSaveAll}
                    loading={saving}
                  />
                </div>
                <Tree
                  treeData={treeData}
                  blockNode
                  draggable
                  selectedKeys={selectedKeys}
                  onSelect={onTreeSelect}
                  allowDrop={allowDrop}
                  onDrop={onDrop}
                  defaultExpandAll={false}
                />
              </div>
            ),
          },
          {
            name: 'Details',
            element: (
              <div className="booking-flow-detail-panel">
                {selectedOption && !selectedConcern && (
                  <OptionDetails
                    option={selectedOption}
                    onChange={onOptionChange}
                    key={selectedOption.id || 'new'}
                  />
                )}
                {selectedConcern && (
                  <ConcernDetails
                    concern={selectedConcern}
                    onChange={onConcernChange}
                    plans={orderedPlans}
                    key={selectedConcern.id}
                  />
                )}
                {!selectedOption && !selectedConcern && (
                  <div className="booking-flow-detail-empty padded-box">
                    Select a type or concern from the list to edit it.
                  </div>
                )}
              </div>
            ),
          },
        ]}
      />
    </div>
  );
};

const OptionDetails = ({ option, onChange: onSync }) => {
  const [value, setValue] = useState(PerformanceUtils.clone(option));

  // Sync every local change back to the parent
  useEffect(() => {
    onSync?.(value);
  }, [value]); // eslint-disable-line react-hooks/exhaustive-deps

  function onChange(event) {
    setValue({ ...value, [event.target.name]: event.target.value });
  }

  return (
    <div className="option-details scroll-list padded-box flex-grow">
      <div className="row">
        <DefaultTextField
          name="title"
          label="Title"
          style={INPUT_STYLE}
          value={value.title}
          onChange={onChange}
        />
      </div>
      <div className="row">
        <DefaultTextField
          name="text"
          label="Text"
          style={INPUT_STYLE}
          value={value.text}
          onChange={onChange}
        />
      </div>
      <div className="row">
        <DefaultSwitch
          name="disabled"
          onChange={(event, isInputChecked) => {
            setValue((prevState) => ({
              ...prevState,
              disabled: isInputChecked,
            }));
          }}
          label="Disabled"
          padded={false}
          checked={!!value.disabled}
        />
      </div>
      <div className="row">
        <DefaultTextField
          name="order"
          label="Order"
          style={INPUT_STYLE}
          value={value.order}
          onChange={onChange}
        />
      </div>
      <DefaultTextField
        name="icon"
        label="Icon"
        style={{ width: '100%' }}
        value={value.icon}
        onChange={(event) => {
          setValue((v) => ({
            ...v,
            icon: event.target.value,
          }));
        }}
      />
      {value.icon && (
        <div
          style={{
            fontWeight: 100,
            color: '#b4b4b4',
            fontSize: '.75rem',
            marginTop: '15px',
          }}
        >
          Icon Preview
          <Icon src={value.icon} width="30px" height="30px" />
        </div>
      )}
    </div>
  );
};

const INPUT_STYLE = { width: '300px', marginRight: '20px' };

const ConcernDetails = ({ concern = {}, onChange: onSync, plans }) => {
  const appointmentOptions = useAppSelector((state) => state.appointments.options);
  const clinics = useAppSelector((state) => state.resource.clinics);
  const templates = useAppSelector((state) => state.templates.templates);

  const [value, setValue] = useState(PerformanceUtils.clone(concern));

  const templateDescriptions = useMemo(() => templates.map((t) => t.description), [templates]);
  const selectedTemplateDescription = useMemo(
    () => templates.find((t) => t.id === value.patient_filled_document)?.description || '',
    [templates, value.patient_filled_document],
  );
  const [templateSearchText, setTemplateSearchText] = useState(selectedTemplateDescription);
  useEffect(() => {
    setTemplateSearchText(selectedTemplateDescription);
  }, [selectedTemplateDescription]);

  // Sync every local change back to the parent
  useEffect(() => {
    onSync?.(normalizeConcern(value));
  }, [value]); // eslint-disable-line react-hooks/exhaustive-deps

  const [fee, setFee] = useState({});
  const [newSupertype, setNewSupertype] = useState();
  // Raw text so separators can be typed; the parsed list lives in value.
  const [digestEmailsText, setDigestEmailsText] = useState(
    (concern.schedule_digest_emails || []).join(', '),
  );

  function onChange(event) {
    setValue((value) => ({ ...value, [event.target.name]: event.target.value }));
  }

  const allSupertypes = useMemo(() => {
    return _(appointmentOptions)
      .flatMap('concerns')
      .map('supertype')
      .filter(Boolean)
      .uniq()
      .value();
  }, [appointmentOptions]);

  const supertypes = useMemo(() => {
    return _([...allSupertypes, SUPERTYPE_ADD_NEW, value.supertype])
      .filter(Boolean)
      .uniq()
      .value();
  }, [allSupertypes, value]);

  const bookingResources = useMemo(() => {
    return _(clinics || [])
      .flatMap((c) => c.booking_resources || [])
      .uniqBy('id')
      .value();
  }, [clinics]);

  const { clinic, video, home, lab } = AppointmentUtils.getAppointmentTypes();
  const { phone: phoneMethod, video: videoMethod } = AppointmentUtils.getTelemedicineMethod();

  // --- Delivery options helpers (new multi-toggle) ---
  function isDeliveryToggled(type, telemedicineMethod) {
    return (value.delivery_options || []).some(
      (opt) =>
        opt.type === type &&
        (!telemedicineMethod || opt.telemedicine_method === telemedicineMethod),
    );
  }

  function toggleDelivery(type, toggled, telemedicineMethod) {
    setValue((prev) => {
      let options = [...(prev.delivery_options || [])];
      if (toggled) {
        options.push({
          type,
          ...(telemedicineMethod ? { telemedicine_method: telemedicineMethod } : {}),
        });
      } else {
        options = options.filter(
          (opt) =>
            !(
              opt.type === type &&
              (!telemedicineMethod || opt.telemedicine_method === telemedicineMethod)
            ),
        );
      }
      return { ...prev, delivery_options: options };
    });
  }

  function getDeliveryMinutes(type, telemedicineMethod) {
    const match = (value.delivery_options || []).find(
      (opt) =>
        opt.type === type &&
        (!telemedicineMethod || opt.telemedicine_method === telemedicineMethod),
    );
    return match?.minutes ?? '';
  }

  function setDeliveryMinutes(type, raw, telemedicineMethod) {
    setValue((prev) => {
      const options = (prev.delivery_options || []).map((opt) => {
        if (
          opt.type === type &&
          (!telemedicineMethod || opt.telemedicine_method === telemedicineMethod)
        ) {
          const parsed = parseInt(raw, 10);
          if (Number.isFinite(parsed) && parsed > 0) return { ...opt, minutes: parsed };
          // Empty/0/NaN means "no override" — strip the field so the concern
          // default applies, rather than storing a duration of zero.
          const { minutes, ...rest } = opt;
          return rest;
        }
        return opt;
      });
      return { ...prev, delivery_options: options };
    });
  }

  function isDeliverySubscribersOnly(type, telemedicineMethod) {
    return (value.delivery_options || []).some(
      (opt) =>
        opt.type === type &&
        (!telemedicineMethod || opt.telemedicine_method === telemedicineMethod) &&
        opt.only_subscribers,
    );
  }

  function toggleDeliverySubscribersOnly(type, checked, telemedicineMethod) {
    setValue((prev) => {
      const options = (prev.delivery_options || []).map((opt) => {
        if (
          opt.type === type &&
          (!telemedicineMethod || opt.telemedicine_method === telemedicineMethod)
        ) {
          return { ...opt, only_subscribers: checked };
        }
        return opt;
      });
      return { ...prev, delivery_options: options };
    });
  }

  function isDeliverySkipFollowUp(type, telemedicineMethod) {
    return (value.delivery_options || []).some(
      (opt) =>
        opt.type === type &&
        (!telemedicineMethod || opt.telemedicine_method === telemedicineMethod) &&
        opt.skip_follow_up,
    );
  }

  function toggleDeliverySkipFollowUp(type, checked, telemedicineMethod) {
    setValue((prev) => {
      const options = (prev.delivery_options || []).map((opt) => {
        if (
          opt.type === type &&
          (!telemedicineMethod || opt.telemedicine_method === telemedicineMethod)
        ) {
          return { ...opt, skip_follow_up: checked };
        }
        return opt;
      });
      return { ...prev, delivery_options: options };
    });
  }

  function isDeliveryHiddenFromPatient(type, telemedicineMethod) {
    return (value.delivery_options || []).some(
      (opt) =>
        opt.type === type &&
        (!telemedicineMethod || opt.telemedicine_method === telemedicineMethod) &&
        opt.hidden_from_patient,
    );
  }

  function toggleDeliveryHiddenFromPatient(type, checked, telemedicineMethod) {
    setValue((prev) => {
      const options = (prev.delivery_options || []).map((opt) => {
        if (
          opt.type === type &&
          (!telemedicineMethod || opt.telemedicine_method === telemedicineMethod)
        ) {
          return { ...opt, hidden_from_patient: checked };
        }
        return opt;
      });
      return { ...prev, delivery_options: options };
    });
  }

  const hasFollowUp = !!value.follow_up_concern?.concern_id;

  const weekdays = useMemo(() => {
    let weekArray = moment.weekdays();
    return weekArray.map((day, index) => {
      return {
        value: index,
        label: day,
      };
    });
  }, []);

  function changeRestriction(index, key, newVal) {
    let newRestrictions = value.restrictions || [];
    newRestrictions[index][key] = newVal;
    setValue({ ...value, restrictions: newRestrictions });
  }

  function deleteFee(optionFee) {
    setValue({
      ...value,
      fees: PerformanceUtils.removeFromArray(optionFee, value.fees, 'plan_id'),
    });
  }

  function getPlanName(plan) {
    if (plan) {
      let nameStr = plan.payment_plan.name;
      let intervalStr = plan.payment_plan.interval ? `, ${plan.payment_plan.interval}` : '';
      let archivedStr = intervalStr ? (plan.payment_plan.active ? '' : ' (Archived)') : '';
      return `${nameStr}${intervalStr} ${plan.id} ${archivedStr}`;
    }
    return 'Unknown Plan';
  }

  function describeCoveredInterval(coveredInterval) {
    if (!coveredInterval?.value) return '';
    const plural = coveredInterval.value !== 1;
    const unit = plural ? coveredInterval.interval : coveredInterval.interval?.slice(0, -1);
    return ` (free every ${coveredInterval.value} ${unit}, charged if sooner)`;
  }

  function getFeeDescription(optionFee) {
    let plan = plans.find((plan) => plan.id === optionFee.plan_id);
    return (
      getPlanName(plan) +
      ' - $' +
      optionFee.fee +
      describeCoveredInterval(optionFee.covered_interval)
    );
  }

  function addFee() {
    if (!!fee.plan_id && !!fee.fee) {
      setValue({
        ...value,
        fees: [
          ...(value.fees || []),
          {
            plan_id: fee.plan_id,
            fee: parseFloat(fee.fee),
            // Only send the window when it's actually set — an empty one would
            // read as "flat fee" server-side anyway, but keep the record clean.
            ...(fee.covered_interval?.value ? { covered_interval: fee.covered_interval } : {}),
          },
        ],
      });
      setFee({});
    }
  }

  return (
    <div className="concern-details scroll-list padded-box">
      {/* Basic Information */}
      <div className="section-group">
        <h3 className="section-title">Basic Information</h3>
        <div className="row">
          <DefaultTextField
            name="text"
            label="Text"
            style={INPUT_STYLE}
            value={value.text}
            onChange={onChange}
          />
          <PhysicianTypes
            concern={value}
            onChange={(event) => {
              setValue((prevState) => {
                return {
                  ...prevState,
                  physician_types: event.target.value,
                };
              });
            }}
          />
        </div>

        <div className="row gap-2">
          <DefaultTooltip
            placement="top"
            title="The bookable footprint of this appointment — the no-overlap window the patient occupies the room/physician. Other appointments cannot start during this window. Stored as the appointment's date_end."
          >
            <span>
              <DefaultTextField
                name="minutes"
                label="Minutes"
                style={{ ...INPUT_STYLE, width: '50px' }}
                value={value.minutes}
                onChange={onChange}
              />
            </span>
          </DefaultTooltip>
          {!value.booking_resource_id && (
            <DefaultTooltip
              placement="top"
              title="How many appointments can share the resource (e.g. one IV bag for two patients at the same time). Counted across all concerns in the same Supertype; if no Supertype is set, the cap is scoped to this concern only. Leave blank for no cap."
            >
              <span>
                <DefaultTextField
                  name="max_simultaneous_appointments"
                  label="Max Appointments"
                  style={{ ...INPUT_STYLE, width: '150px' }}
                  value={value.max_simultaneous_appointments}
                  onChange={onChange}
                />
              </span>
            </DefaultTooltip>
          )}
          <DefaultTooltip
            placement="top"
            title="How long the shared resource stays in use, in minutes (e.g. a 30-minute IV start keeps the IV chair for 120 minutes). With a Booking resource set, the clinic's count is the cap; otherwise used together with Max Appointments. Defaults to Minutes when blank."
          >
            <span>
              <DefaultTextField
                name="capacity_duration_minutes"
                label="Total Duration"
                style={{ ...INPUT_STYLE, width: '130px' }}
                value={value.capacity_duration_minutes}
                onChange={onChange}
              />
            </span>
          </DefaultTooltip>
          <DefaultSwitch
            name="disabled"
            reversed={true}
            onChange={(event, isInputChecked) => {
              setValue((prevState) => {
                return {
                  ...prevState,
                  disabled: isInputChecked,
                };
              });
            }}
            label="Disabled"
            padded={false}
            checked={!!value.disabled}
          />
        </div>
        <div className="row">
          <DefaultTextField
            name="cancel_cutoff"
            label="Cancel Cutoff (minutes)"
            style={{ ...INPUT_STYLE, width: '200px' }}
            value={value.cancel_cutoff}
            onChange={(ev) => {
              let value = parseInt(ev.target.value, 10);
              if (Number.isNaN(value)) {
                value = undefined;
              }
              onChange({
                target: {
                  name: 'cancel_cutoff',
                  value,
                },
              });
            }}
          />
          <DefaultTextField
            name="refund_window_hours"
            label="Refund Window (hours)"
            placeholder="4"
            style={{ ...INPUT_STYLE, width: '200px' }}
            value={value.refund_window_hours}
            onChange={(ev) => {
              let value = parseInt(ev.target.value, 10);
              if (Number.isNaN(value)) {
                value = undefined;
              } else {
                value = Math.min(168, Math.max(0, value)); // clamp 0..168h
              }
              onChange({
                target: {
                  name: 'refund_window_hours',
                  value,
                },
              });
            }}
          />
        </div>
        <div className="row">
          <DefaultTextField
            name="blocked_minutes_before"
            label="Blocked Before (minutes)"
            placeholder="Home visits: 30"
            style={{ ...INPUT_STYLE, width: '200px' }}
            value={value.blocked_minutes_before}
            onChange={(ev) => {
              let value = parseInt(ev.target.value, 10);
              if (Number.isNaN(value) || value < 0) {
                value = undefined;
              }
              onChange({
                target: {
                  name: 'blocked_minutes_before',
                  value,
                },
              });
            }}
          />
          <DefaultTextField
            name="blocked_minutes_after"
            label="Blocked After (minutes)"
            placeholder="Home visits: 30"
            style={{ ...INPUT_STYLE, width: '200px' }}
            value={value.blocked_minutes_after}
            onChange={(ev) => {
              let value = parseInt(ev.target.value, 10);
              if (Number.isNaN(value) || value < 0) {
                value = undefined;
              }
              onChange({
                target: {
                  name: 'blocked_minutes_after',
                  value,
                },
              });
            }}
          />
        </div>
        <div className="row">
          <TimeIntervalField
            label="Minimum Interval"
            value={value.minimum_interval}
            onChange={(val) => {
              onChange({
                target: {
                  name: 'minimum_interval',
                  value: val,
                },
              });
            }}
            min_interval="days"
            valueWidth="150px"
          />
        </div>
        <div className="row gap-2">
          <DefaultTooltip
            placement="top"
            title="Groups concerns that share a resource (e.g. all IV concerns under the 'IV' supertype share the same IV bag and the same Max Appointments cap). Also enforces 'only one of this kind at a time' on the patient's schedule. Leave blank to keep this concern in its own bucket."
          >
            <span style={{ flex: 1 }}>
              <DefaultSelectField
                key={value.supertype}
                label="Supertype"
                style={{ ...INPUT_STYLE }}
                value={value.supertype}
                onChange={(ev) => {
                  setValue((prev) => ({
                    ...prev,
                    supertype: ev.target.value,
                  }));
                }}
                name="supertype"
                inputs={supertypes}
              />
            </span>
          </DefaultTooltip>
          <DefaultTooltip
            placement="top"
            title="The clinic resource this concern occupies (e.g. IV chair). Overlapping bookings are capped at the clinic's count for that resource, across all concerns using it and across practitioners. Total Duration = how long it stays occupied. Leave as None for no resource."
          >
            <span style={{ flex: 1 }}>
              <DefaultSelectField
                key={value.booking_resource_id || 'none'}
                label="Booking resource"
                style={{ ...INPUT_STYLE }}
                value={value.booking_resource_id || ''}
                onChange={(ev) => {
                  const booking_resource_id = ev.target.value || undefined;
                  setValue((prev) => ({ ...prev, booking_resource_id }));
                }}
                name="booking_resource_id"
                inputElements={[
                  <MenuItem value="" key="none">
                    None
                  </MenuItem>,
                  ...bookingResources.map((r) => (
                    <MenuItem value={r.id} key={r.id}>
                      {r.name} ({r.id})
                    </MenuItem>
                  )),
                ]}
              />
            </span>
          </DefaultTooltip>
        </div>
        <div className="flex gap-2 items-end">
          <DefaultTextField
            name="color_on_schedule"
            label="Color on Schedule"
            style={{ ...INPUT_STYLE }}
            value={value.color_on_schedule}
            onChange={(event) =>
              setValue((prev) => ({ ...prev, color_on_schedule: event.target.value }))
            }
          />
          <div
            style={{
              borderRadius: '7px',
              border: '2px solid var(--color-border-default)',
              aspectRatio: '1',
              height: '30px',
              backgroundColor: value.color_on_schedule ?? 'white',
            }}
          />
        </div>
        <div className="row gap-2">
          <DefaultSwitch
            name="hidden_from_patient"
            onChange={(event, isInputChecked) => {
              setValue((prevState) => {
                return {
                  ...prevState,
                  hidden_from_patient: isInputChecked,
                };
              });
            }}
            label="Hide from patient"
            padded={false}
            checked={!!value.hidden_from_patient}
          />
        </div>
        <div className="row">
          <DefaultTextField
            name="invoice_item_name"
            label="Invoice Item Name (optional)"
            placeholder="e.g. Initial Consultation"
            style={INPUT_STYLE}
            value={value.invoice_item_name}
            onChange={onChange}
          />
          <DefaultTextField
            name="invoice_code"
            label="Invoice Code"
            placeholder="Auto-generated"
            style={{ ...INPUT_STYLE, width: '130px' }}
            value={value.invoice_code || ''}
            onChange={(event) => {
              const code = event.target.value
                .toUpperCase()
                .replace(/[^A-Z0-9]/g, '')
                .slice(0, 8);
              setValue((prev) => ({ ...prev, invoice_code: code }));
            }}
          />
        </div>
        <div className="row gap-2">
          <DefaultSwitch
            name="manual_charge"
            onChange={(event, isInputChecked) => {
              setValue((prevState) => {
                return {
                  ...prevState,
                  manual_charge: isInputChecked,
                };
              });
            }}
            label="Manual charge (record fee at booking, charge later)"
            padded={false}
            checked={!!value.manual_charge}
          />
        </div>
        <div className="row gap-2">
          {value.supertype === SUPERTYPE_ADD_NEW && (
            <>
              <DefaultTextField
                name="new_supertype"
                label="Supertype Name"
                style={{ ...INPUT_STYLE }}
                value={newSupertype}
                onChange={(event) => setNewSupertype(event.target.value)}
              />
              <Icon
                src="circle-plus"
                width="25px"
                alt="Add Supertype"
                className="pt-5 blue mt-1"
                showTooltip={true}
                style={{ display: 'inline-block' }}
                onClick={() => {
                  setValue((prev) => ({ ...prev, supertype: newSupertype }));
                }}
              />
            </>
          )}
        </div>
      </div>
      {/* Appointment Configuration */}
      <div className="section-group">
        <h3 className="section-title">Appointment Configuration</h3>
        <div className="subsection">
          <h4 className="subsection-title">Delivery Options</h4>
          <div className="delivery-options-grid">
            <div className="delivery-option-row">
              <DefaultSwitch
                name="delivery_clinic"
                onChange={(event, isInputChecked) => toggleDelivery(clinic, isInputChecked)}
                label="Clinic"
                padded={false}
                checked={isDeliveryToggled(clinic)}
              />
              {isDeliveryToggled(clinic) && (
                <DefaultTextField
                  name="delivery_clinic_minutes"
                  label="Minutes"
                  placeholder={String(value.minutes || '')}
                  style={{ ...INPUT_STYLE, width: '60px' }}
                  value={getDeliveryMinutes(clinic)}
                  onChange={(event) => setDeliveryMinutes(clinic, event.target.value)}
                />
              )}
              {isDeliveryToggled(clinic) && (
                <DefaultSwitch
                  name="delivery_clinic_subs"
                  onChange={(event, isInputChecked) =>
                    toggleDeliverySubscribersOnly(clinic, isInputChecked)
                  }
                  label="Subscribers Only"
                  padded={false}
                  checked={isDeliverySubscribersOnly(clinic)}
                />
              )}
              {isDeliveryToggled(clinic) && (
                <DefaultSwitch
                  name="delivery_clinic_hidden"
                  onChange={(event, isInputChecked) =>
                    toggleDeliveryHiddenFromPatient(clinic, isInputChecked)
                  }
                  label="Hidden from Patient"
                  padded={false}
                  checked={isDeliveryHiddenFromPatient(clinic)}
                />
              )}
              {isDeliveryToggled(clinic) && hasFollowUp && (
                <DefaultSwitch
                  name="delivery_clinic_skip_follow_up"
                  onChange={(event, isInputChecked) =>
                    toggleDeliverySkipFollowUp(clinic, isInputChecked)
                  }
                  label="Skip follow-up"
                  padded={false}
                  checked={isDeliverySkipFollowUp(clinic)}
                />
              )}
            </div>
            <div className="delivery-option-row">
              <DefaultSwitch
                name="delivery_video"
                onChange={(event, isInputChecked) =>
                  toggleDelivery(video, isInputChecked, videoMethod)
                }
                label="Video"
                padded={false}
                checked={isDeliveryToggled(video, videoMethod)}
              />
              {isDeliveryToggled(video, videoMethod) && (
                <DefaultTextField
                  name="delivery_video_minutes"
                  label="Minutes"
                  placeholder={String(value.minutes || '')}
                  style={{ ...INPUT_STYLE, width: '60px' }}
                  value={getDeliveryMinutes(video, videoMethod)}
                  onChange={(event) => setDeliveryMinutes(video, event.target.value, videoMethod)}
                />
              )}
              {isDeliveryToggled(video, videoMethod) && (
                <DefaultSwitch
                  name="delivery_video_subs"
                  onChange={(event, isInputChecked) =>
                    toggleDeliverySubscribersOnly(video, isInputChecked, videoMethod)
                  }
                  label="Subscribers Only"
                  padded={false}
                  checked={isDeliverySubscribersOnly(video, videoMethod)}
                />
              )}
              {isDeliveryToggled(video, videoMethod) && (
                <DefaultSwitch
                  name="delivery_video_hidden"
                  onChange={(event, isInputChecked) =>
                    toggleDeliveryHiddenFromPatient(video, isInputChecked, videoMethod)
                  }
                  label="Hidden from Patient"
                  padded={false}
                  checked={isDeliveryHiddenFromPatient(video, videoMethod)}
                />
              )}
              {isDeliveryToggled(video, videoMethod) && hasFollowUp && (
                <DefaultSwitch
                  name="delivery_video_skip_follow_up"
                  onChange={(event, isInputChecked) =>
                    toggleDeliverySkipFollowUp(video, isInputChecked, videoMethod)
                  }
                  label="Skip follow-up"
                  padded={false}
                  checked={isDeliverySkipFollowUp(video, videoMethod)}
                />
              )}
            </div>
            <div className="delivery-option-row">
              <DefaultSwitch
                name="delivery_phone"
                onChange={(event, isInputChecked) =>
                  toggleDelivery(video, isInputChecked, phoneMethod)
                }
                label="Phone"
                padded={false}
                checked={isDeliveryToggled(video, phoneMethod)}
              />
              {isDeliveryToggled(video, phoneMethod) && (
                <DefaultTextField
                  name="delivery_phone_minutes"
                  label="Minutes"
                  placeholder={String(value.minutes || '')}
                  style={{ ...INPUT_STYLE, width: '60px' }}
                  value={getDeliveryMinutes(video, phoneMethod)}
                  onChange={(event) => setDeliveryMinutes(video, event.target.value, phoneMethod)}
                />
              )}
              {isDeliveryToggled(video, phoneMethod) && (
                <DefaultSwitch
                  name="delivery_phone_subs"
                  onChange={(event, isInputChecked) =>
                    toggleDeliverySubscribersOnly(video, isInputChecked, phoneMethod)
                  }
                  label="Subscribers Only"
                  padded={false}
                  checked={isDeliverySubscribersOnly(video, phoneMethod)}
                />
              )}
              {isDeliveryToggled(video, phoneMethod) && (
                <DefaultSwitch
                  name="delivery_phone_hidden"
                  onChange={(event, isInputChecked) =>
                    toggleDeliveryHiddenFromPatient(video, isInputChecked, phoneMethod)
                  }
                  label="Hidden from Patient"
                  padded={false}
                  checked={isDeliveryHiddenFromPatient(video, phoneMethod)}
                />
              )}
              {isDeliveryToggled(video, phoneMethod) && hasFollowUp && (
                <DefaultSwitch
                  name="delivery_phone_skip_follow_up"
                  onChange={(event, isInputChecked) =>
                    toggleDeliverySkipFollowUp(video, isInputChecked, phoneMethod)
                  }
                  label="Skip follow-up"
                  padded={false}
                  checked={isDeliverySkipFollowUp(video, phoneMethod)}
                />
              )}
            </div>
            <div className="delivery-option-row">
              <DefaultSwitch
                name="delivery_home"
                onChange={(event, isInputChecked) => toggleDelivery(home, isInputChecked)}
                label="Home"
                padded={false}
                checked={isDeliveryToggled(home)}
              />
              {isDeliveryToggled(home) && (
                <DefaultTextField
                  name="delivery_home_minutes"
                  label="Minutes"
                  placeholder={String(value.minutes || '')}
                  style={{ ...INPUT_STYLE, width: '60px' }}
                  value={getDeliveryMinutes(home)}
                  onChange={(event) => setDeliveryMinutes(home, event.target.value)}
                />
              )}
              {isDeliveryToggled(home) && (
                <DefaultSwitch
                  name="delivery_home_subs"
                  onChange={(event, isInputChecked) =>
                    toggleDeliverySubscribersOnly(home, isInputChecked)
                  }
                  label="Subscribers Only"
                  padded={false}
                  checked={isDeliverySubscribersOnly(home)}
                />
              )}
              {isDeliveryToggled(home) && (
                <DefaultSwitch
                  name="delivery_home_hidden"
                  onChange={(event, isInputChecked) =>
                    toggleDeliveryHiddenFromPatient(home, isInputChecked)
                  }
                  label="Hidden from Patient"
                  padded={false}
                  checked={isDeliveryHiddenFromPatient(home)}
                />
              )}
              {isDeliveryToggled(home) && hasFollowUp && (
                <DefaultSwitch
                  name="delivery_home_skip_follow_up"
                  onChange={(event, isInputChecked) =>
                    toggleDeliverySkipFollowUp(home, isInputChecked)
                  }
                  label="Skip follow-up"
                  padded={false}
                  checked={isDeliverySkipFollowUp(home)}
                />
              )}
            </div>
            <div className="delivery-option-row">
              <DefaultSwitch
                name="delivery_lab"
                onChange={(event, isInputChecked) => toggleDelivery(lab, isInputChecked)}
                label="Lab"
                padded={false}
                checked={isDeliveryToggled(lab)}
              />
              {isDeliveryToggled(lab) && (
                <DefaultTextField
                  name="delivery_lab_minutes"
                  label="Minutes"
                  placeholder={String(value.minutes || '')}
                  style={{ ...INPUT_STYLE, width: '60px' }}
                  value={getDeliveryMinutes(lab)}
                  onChange={(event) => setDeliveryMinutes(lab, event.target.value)}
                />
              )}
              {isDeliveryToggled(lab) && (
                <DefaultSwitch
                  name="delivery_lab_subs"
                  onChange={(event, isInputChecked) =>
                    toggleDeliverySubscribersOnly(lab, isInputChecked)
                  }
                  label="Subscribers Only"
                  padded={false}
                  checked={isDeliverySubscribersOnly(lab)}
                />
              )}
              {isDeliveryToggled(lab) && (
                <DefaultSwitch
                  name="delivery_lab_hidden"
                  onChange={(event, isInputChecked) =>
                    toggleDeliveryHiddenFromPatient(lab, isInputChecked)
                  }
                  label="Hidden from Patient"
                  padded={false}
                  checked={isDeliveryHiddenFromPatient(lab)}
                />
              )}
              {isDeliveryToggled(lab) && hasFollowUp && (
                <DefaultSwitch
                  name="delivery_lab_skip_follow_up"
                  onChange={(event, isInputChecked) =>
                    toggleDeliverySkipFollowUp(lab, isInputChecked)
                  }
                  label="Skip follow-up"
                  padded={false}
                  checked={isDeliverySkipFollowUp(lab)}
                />
              )}
            </div>
          </div>
        </div>
        <div className="subsection">
          <h4 className="subsection-title">Fees</h4>
          {/* Bulk add to all plans */}
          <div
            className="row mb-3"
            style={{ borderBottom: '1px solid var(--color-border-default)', paddingBottom: '10px' }}
          >
            <DefaultTextField
              name="bulkFee"
              label="Set All Plans Fee"
              style={{ ...INPUT_STYLE, width: '150px' }}
              value={fee.bulkFee || ''}
              onChange={(event) => setFee({ ...fee, bulkFee: event.target.value })}
            />
            <DefaultTooltip
              placement="top"
              title="Optional. Leave blank for a flat fee. Set it to make the visit free once per period and charge the fee only when the patient had a free one inside it — e.g. 24 months + $250 means one free every 24 months, $250 if sooner. The clock runs from their last FREE visit of this type, so paying for an early one doesn't push the next free one out."
            >
              <span>
                <TimeIntervalField
                  label="Free every (all plans)"
                  value={fee.bulkCoveredInterval || null}
                  onChange={(val) => setFee({ ...fee, bulkCoveredInterval: val })}
                  min_interval="days"
                  valueWidth="110px"
                />
              </span>
            </DefaultTooltip>
            <DefaultButton
              name="Apply to All Plans"
              className="blue"
              style={{ marginTop: '18px' }}
              onClick={() => {
                if (fee.bulkFee) {
                  const allPlans = plans.filter((plan) => {
                    // Only include plans that haven't been added yet
                    return !(value.fees || []).some(
                      (existingFee) => existingFee.plan_id === plan.id,
                    );
                  });

                  const newFees = allPlans.map((plan) => ({
                    plan_id: plan.id,
                    fee: parseFloat(fee.bulkFee),
                    ...(fee.bulkCoveredInterval?.value
                      ? { covered_interval: fee.bulkCoveredInterval }
                      : {}),
                  }));

                  setValue({
                    ...value,
                    fees: [...(value.fees || []), ...newFees],
                  });
                  setFee({ ...fee, bulkFee: '', bulkCoveredInterval: null });
                }
              }}
            />
          </div>
          {/* Individual plan add */}
          <div className="row">
            <DefaultSelectField
              label="Plan"
              style={{ ...INPUT_STYLE, width: '250px' }}
              value={fee.plan_id}
              onChange={(event) => {
                setFee((fee) => ({ ...fee, plan_id: event.target.value }));
              }}
              inputElements={plans
                .filter((plan) => {
                  // Filter out plans that have already been added
                  return !(value.fees || []).some((existingFee) => existingFee.plan_id === plan.id);
                })
                .map((plan) => {
                  return (
                    <MenuItem value={plan.id} key={'select-concern-' + plan.id}>
                      {getPlanName(plan)}
                    </MenuItem>
                  );
                })}
            />
            <DefaultTextField
              name="fee"
              label="Fee"
              style={{ ...INPUT_STYLE, width: '50px' }}
              value={fee.fee || ''}
              onChange={(event) => setFee({ ...fee, fee: event.target.value })}
            />
            <DefaultTooltip
              placement="top"
              title="Optional. Leave blank for a flat fee. Set it to make the visit free once per period and charge the fee only when the patient had a free one inside it — e.g. 24 months + $250 means one free every 24 months, $250 if sooner. The clock runs from their last FREE visit of this type, so paying for an early one doesn't push the next free one out."
            >
              <span>
                <TimeIntervalField
                  label="Free every"
                  value={fee.covered_interval || null}
                  onChange={(val) => setFee({ ...fee, covered_interval: val })}
                  min_interval="days"
                  valueWidth="90px"
                />
              </span>
            </DefaultTooltip>
            <Icon
              src="circle-plus"
              width="25px"
              alt="Add Fee"
              className="pt-5 blue mt-1"
              showTooltip={true}
              style={{ display: 'inline-block' }}
              onClick={addFee}
            />
          </div>
          <StackItemsView
            stack={value.fees || []}
            onDeleteItem={deleteFee}
            describeItem={getFeeDescription}
            onEditItem={setFee}
          />
        </div>
      </div>
      {/* Booking Flow Configuration */}
      <div className="section-group">
        <h3 className="section-title">Booking Flow Configuration</h3>
        <div className="subsection">
          <h4 className="subsection-title">Flow Options</h4>
          <DefaultSwitch
            name="skip_reason"
            reversed={true}
            onChange={(event, isInputChecked) => {
              setValue((prevState) => {
                return {
                  ...prevState,
                  skip_reason: isInputChecked,
                };
              });
            }}
            label={'Skip "Reason for Consultation"'}
            padded={false}
            checked={!!value.skip_reason}
          />
          <DefaultSwitch
            name="allow_empty_reason"
            reversed={true}
            onChange={(event, isInputChecked) => {
              setValue((prevState) => {
                return {
                  ...prevState,
                  allow_empty_reason: isInputChecked,
                };
              });
            }}
            label={'Allow Empty "Reason for Consultation"'}
            padded={false}
            checked={!!value.allow_empty_reason}
          />
          {!value.skip_reason && (
            <>
              <span className="small-title">"Reason for Consultation" prompt text</span>
              <TextareaAutosize
                name="reason_text"
                placeholder="What is the reason for your appointment?"
                style={{ ...INPUT_STYLE, width: '100%' }}
                value={value.reason_text || ''}
                onChange={(event) => {
                  let text = event.target.value;
                  setValue((prevState) => {
                    return {
                      ...prevState,
                      reason_text: text,
                    };
                  });
                }}
              />
            </>
          )}
          <span className="small-title">AI Prompt</span>
          <div className="">
            <TextareaAutosize
              name="ai_prompt"
              label="AI Prompt"
              style={{ ...INPUT_STYLE, width: '100%' }}
              value={value.ai_prompt || ''}
              onChange={(event) => {
                let text = event.target.value;
                setValue((prevState) => {
                  return {
                    ...prevState,
                    ai_prompt: text,
                  };
                });
              }}
            />
          </div>
          <span className="small-title">
            Additional text for SMS reminders and push notifications (plain text)
          </span>
          <div className="">
            <TextareaAutosize
              name="communication_text"
              style={{ ...INPUT_STYLE, width: '100%' }}
              value={value.communication_text || ''}
              onChange={(event) => {
                let text = event.target.value;
                setValue((prevState) => {
                  return {
                    ...prevState,
                    communication_text: text,
                  };
                });
              }}
            />
          </div>
          <span className="small-title">
            Additional content for new-appointment confirmation email (markdown)
          </span>
          <div className="">
            <Suspense fallback={<div>Loading editor...</div>}>
              <RichTextEditable
                value={value.email_communication_markdown || ''}
                onChange={(markdown) => {
                  setValue((prevState) => {
                    return {
                      ...prevState,
                      email_communication_markdown: markdown,
                    };
                  });
                }}
                placeholder="Markdown rendered into the appointment confirmation email footer..."
              />
            </Suspense>
          </div>
          <span className="small-title">
            Daily schedule email recipients (comma or newline separated). Each address gets an 8:30
            AM email every business day listing this appointment type&apos;s bookings for the next 7
            business days, with new bookings in bold.
          </span>
          <div className="">
            <TextareaAutosize
              name="schedule_digest_emails"
              placeholder="e.g. pharmacy@example.com"
              style={{ ...INPUT_STYLE, width: '100%' }}
              value={digestEmailsText}
              onChange={(event) => {
                const text = event.target.value;
                setDigestEmailsText(text);
                const emails = text
                  .split(/[,;\s]+/)
                  .map((email) => email.trim())
                  .filter(Boolean);
                // [] (not undefined) clears: the server keeps stored
                // recipients when the field is absent.
                setValue((prevState) => ({ ...prevState, schedule_digest_emails: emails }));
              }}
            />
          </div>
          <DefaultSwitch
            name="modal.enabled"
            reversed={true}
            onChange={(event, isInputChecked) => {
              setValue((prevState) => {
                return {
                  ...prevState,
                  modal: {
                    ...(prevState.modal || {}),
                    enabled: isInputChecked,
                  },
                };
              });
            }}
            label="Show confirmation modal"
            padded={false}
            checked={!!value.modal?.enabled}
          />
          {!!value.modal?.enabled && (
            <>
              <DefaultTextField
                name="modal.title"
                label="Modal Title"
                style={{ ...INPUT_STYLE, width: '390px' }}
                value={(value.modal || {}).title || ''}
                onChange={(event) => {
                  let title = event.target.value;
                  setValue((prevState) => {
                    return {
                      ...prevState,
                      modal: { ...(value.modal || {}), title },
                    };
                  });
                }}
              />
              <TextareaAutosize
                name="modal.text"
                label="Modal Text"
                style={{ ...INPUT_STYLE, width: '390px' }}
                value={(value.modal || {}).text || ''}
                onChange={(event) => {
                  let text = event.target.value;
                  setValue((prevState) => {
                    return {
                      ...prevState,
                      modal: { ...(value.modal || {}), text },
                    };
                  });
                }}
              />
              <div className="row">
                <DefaultTextField
                  name="modal.option_cancel"
                  label="Modal Option Cancel"
                  style={{ ...INPUT_STYLE, width: '170px' }}
                  value={(value.modal || {}).option_cancel || ''}
                  onChange={(event) => {
                    let option_cancel = event.target.value;
                    setValue((prevState) => {
                      return {
                        ...prevState,
                        modal: { ...(value.modal || {}), option_cancel },
                      };
                    });
                  }}
                />
                <DefaultTextField
                  name="modal.option_confirm"
                  label="Modal Option Confirm"
                  style={{ ...INPUT_STYLE, width: '170px' }}
                  value={(value.modal || {}).option_confirm || ''}
                  onChange={(event) => {
                    let option_confirm = event.target.value;
                    setValue((prevState) => {
                      return {
                        ...prevState,
                        modal: { ...(value.modal || {}), option_confirm },
                      };
                    });
                  }}
                />
              </div>
            </>
          )}
        </div>
      </div>

      {/* Access Control */}
      <div className="section-group">
        <h3 className="section-title">Access Control</h3>
        <div className="subsection">
          <h4 className="subsection-title">Demographics</h4>
          <DefaultSelectField
            label="Gender"
            value={value.gender}
            inputs={['', 'Male', 'Female']}
            onChange={(event) => {
              setValue({
                ...value,
                gender: event.target.value,
              });
            }}
          />
          <div className="row">
            <DefaultTextField
              name="above_age_months"
              label="Above Age (months)"
              value={(value.above_age_months || '') + ''}
              onChange={(event) => {
                setValue({
                  ...value,
                  above_age_months: !event.target.value ? undefined : parseInt(event.target.value),
                });
              }}
              style={{ width: '160px', marginRight: '15px' }}
            />
            <DefaultTextField
              name="below_age_months"
              label="Below Age (months)"
              value={(value.below_age_months || '') + ''}
              onChange={(event) => {
                setValue({
                  ...value,
                  below_age_months: !event.target.value ? undefined : parseInt(event.target.value),
                });
              }}
              style={{ width: '160px' }}
            />
          </div>
        </div>
        <div className="subsection">
          <h4 className="subsection-title">Clinic Restrictions</h4>
          <div className="row">
            <DefaultSelectField
              label="Available Clinics"
              name="clinics"
              width="390px"
              value={value.clinics || []}
              onChange={(event) => {
                setValue((prevState) => ({
                  ...prevState,
                  clinics: event.target.value,
                }));
              }}
              multiple={true}
              inputElements={clinics.map((clinic) => (
                <MenuItem value={clinic.id} key={clinic.id}>
                  {clinic.name}
                </MenuItem>
              ))}
            />
          </div>
          <div
            style={{
              fontWeight: 100,
              color: '#b4b4b4',
              fontSize: '.75rem',
              marginTop: '5px',
              marginBottom: '15px',
            }}
          >
            Leave empty for no restrictions. Select specific clinics to limit this appointment type
            to only those locations.
          </div>
        </div>
        <div className="subsection">
          <h4 className="subsection-title">User Restrictions</h4>
          <DefaultSwitch
            name="only_primary_physician"
            reversed={true}
            onChange={(event, isInputChecked) => {
              setValue((prevState) => {
                return {
                  ...prevState,
                  only_primary_physician: isInputChecked,
                };
              });
            }}
            label="Primary Practitioner Only"
            padded={false}
            checked={!!value.only_primary_physician}
          />
          <DefaultSwitch
            name="no_primary_physician"
            reversed={true}
            onChange={(event, isInputChecked) => {
              setValue((prevState) => {
                return {
                  ...prevState,
                  no_primary_physician: isInputChecked,
                };
              });
            }}
            label="No Primary Practitioner"
            padded={false}
            checked={!!value.no_primary_physician}
          />
          <DefaultSwitch
            name="annual"
            reversed={true}
            onChange={(event, isInputChecked) => {
              setValue((prevState) => {
                return {
                  ...prevState,
                  annual: isInputChecked,
                };
              });
            }}
            label="Annual"
            padded={false}
            checked={!!value.annual}
          />
          <DefaultSwitch
            name="not_first_appointment"
            reversed={true}
            onChange={(event, isInputChecked) => {
              setValue((prevState) => {
                return {
                  ...prevState,
                  not_first_appointment: isInputChecked,
                };
              });
            }}
            label="Not valid first appointment"
            padded={false}
            checked={!!value.not_first_appointment}
          />
        </div>
        <div className="subsection">
          <h4 className="subsection-title">Time Restrictions</h4>
          <div
            className="subsection-description"
            style={{ marginBottom: '8px', color: 'var(--color-text-secondary)' }}
          >
            Clinic-specific restrictions take precedence over a same-weekday restriction with no
            clinic selected.
          </div>
          {(value.restrictions || []).map((restriction, index) => {
            const clinicOptions =
              (value.clinics || []).length > 0
                ? clinics.filter((clinic) => value.clinics.includes(clinic.id))
                : clinics;
            return (
              <div className="row" key={index}>
                <DefaultSelectField
                  name={`value.restrictions[${index}].weekDay`}
                  label="Week Day"
                  value={restriction.weekDay}
                  onChange={(event) => {
                    changeRestriction(index, 'weekDay', event.target.value);
                  }}
                  style={{ width: '150px' }}
                  inputElements={weekdays.map((weekday) => (
                    <MenuItem value={weekday.value} key={weekday.value}>
                      {weekday.label}
                    </MenuItem>
                  ))}
                />
                <DefaultSelectField
                  name={`value.restrictions[${index}].clinic_id`}
                  label="Clinic"
                  value={restriction.clinic_id || ''}
                  onChange={(event) => {
                    changeRestriction(index, 'clinic_id', event.target.value || undefined);
                  }}
                  style={{ width: '200px' }}
                  inputElements={[
                    <MenuItem value="" key="__any__">
                      Any clinic
                    </MenuItem>,
                    ...clinicOptions.map((clinic) => (
                      <MenuItem value={clinic.id} key={clinic.id}>
                        {clinic.name}
                      </MenuItem>
                    )),
                  ]}
                />
                <DefaultSwitch
                  name={`value.restrictions[${index}].disabled`}
                  onChange={(event, isInputChecked) => {
                    changeRestriction(index, 'disabled', isInputChecked);
                    if (isInputChecked) {
                      changeRestriction(index, 'start', '');
                      changeRestriction(index, 'end', '');
                    }
                  }}
                  label="Disabled"
                  padded={true}
                  checked={restriction.disabled}
                />
                {!restriction.disabled && (
                  <>
                    <DefaultTextField
                      name={`value.restrictions[${index}].start`}
                      label="Start (HHMM)"
                      style={{ ...INPUT_STYLE, width: '120px' }}
                      value={restriction.start}
                      onChange={(event) => {
                        changeRestriction(index, 'start', event.target.value);
                      }}
                    />
                    <DefaultTextField
                      name={`value.restrictions[${index}].end`}
                      label="End (HHMM)"
                      style={{ ...INPUT_STYLE, width: '100px' }}
                      value={restriction.end}
                      onChange={(event) => {
                        changeRestriction(index, 'end', event.target.value);
                      }}
                    />
                  </>
                )}
                <Icon
                  src="trash"
                  style={{ margin: 0, display: 'flex' }}
                  onClick={() => {
                    let removedValue = [...value.restrictions];
                    removedValue.splice(index, 1);
                    setValue({ ...value, restrictions: removedValue });
                  }}
                />
              </div>
            );
          })}
          <div className="row my-2">
            <DefaultButton
              name="Add Restriction"
              onClick={() =>
                setValue({
                  ...value,
                  restrictions: [...(value.restrictions || []), { weekDay: 0, disabled: true }],
                })
              }
            />
          </div>
        </div>
      </div>

      {/* Advanced Configuration */}
      <div className="section-group">
        <h3 className="section-title">Advanced Configuration</h3>
        <div className="subsection">
          <h4 className="subsection-title">Required Skills</h4>
          <Skills
            concern={value}
            onChange={(event) => {
              setValue((prevState) => {
                return {
                  ...prevState,
                  skills: event.target.value,
                };
              });
            }}
          />
        </div>
        <RequirementsPicker
          requirements={value.requirements || []}
          onChange={(requirements) => setValue((prev) => ({ ...prev, requirements }))}
        />
        <GrantsClearancesPicker
          grantsClearances={value.grants_clearances || []}
          onChange={(grants_clearances) => setValue((prev) => ({ ...prev, grants_clearances }))}
        />
        <div className="subsection">
          <h4 className="subsection-title">Patient Filled Document</h4>
          <DefaultAutoComplete
            style={{ width: '100%' }}
            placeholder="Select a document template"
            input={{
              value: templateSearchText,
              onChange: (text) => {
                setTemplateSearchText(text);
                const template = templates.find((t) => t.description === text);
                setValue((prev) => ({
                  ...prev,
                  patient_filled_document: template?.id || undefined,
                }));
              },
            }}
            onUpdateInput={(text) => {
              setTemplateSearchText(text);
              if (!text) {
                setValue((prev) => ({ ...prev, patient_filled_document: undefined }));
              }
            }}
            dataSource={templateDescriptions}
            allowClear
          />
        </div>
        <div className="subsection">
          <h4 className="subsection-title">Follow-up Appointment</h4>
          <div style={{ position: 'relative' }}>
            <DefaultSelectField
              label="Concern"
              name="follow_up_concern_id"
              value={value.follow_up_concern?.concern_id}
              onChange={(ev) =>
                setValue((value) => ({
                  ...value,
                  follow_up_concern: {
                    ...(value.follow_up_concern || {}),
                    concern_id: ev.target.value,
                  },
                }))
              }
              style={{
                width: '100%',
                paddingRight: value.follow_up_concern?.concern_id ? '40px' : '0',
              }}
              inputElements={appointmentOptions
                ?.filter((it) => !it.disabled)
                ?.flatMap(
                  (option) =>
                    option.concerns
                      ?.filter((concern) => !concern.disabled)
                      ?.map((concern) => ({ option, concern })) ?? [],
                )
                ?.map(({ option, concern }) => {
                  return (
                    <MenuItem value={concern.id} key={concern.id}>
                      {option.title} : {concern.text}
                    </MenuItem>
                  );
                })}
            />
            {value.follow_up_concern?.concern_id && (
              <Icon
                src="trash"
                width="15px"
                alt="Remove Follow-up Appointment"
                className="red"
                showTooltip={true}
                style={{
                  position: 'absolute',
                  right: '10px',
                  top: '50%',
                  cursor: 'pointer',
                  zIndex: 1,
                }}
                onClick={() => {
                  setValue((value) => ({
                    ...value,
                    follow_up_concern: undefined,
                  }));
                }}
              />
            )}
          </div>
          {value.follow_up_concern?.concern_id && (
            <>
              <DefaultSwitch
                name="follow_up_concern.cancel_all"
                label="Cancel all"
                reversed
                checked={value.follow_up_concern?.options?.cancel_all ?? false}
                onChange={(ev, checked) =>
                  setValue((value) => ({
                    ...value,
                    follow_up_concern: {
                      ...(value.follow_up_concern || {}),
                      options: {
                        ...(value.follow_up_concern?.options || {}),
                        cancel_all: checked,
                      },
                    },
                  }))
                }
              />
              <DefaultSwitch
                name="follow_up_concern.hide_from_patient"
                label="Hide from Patient"
                reversed
                checked={value.follow_up_concern?.options?.hide_from_patient ?? false}
                onChange={(ev, checked) =>
                  setValue((value) => ({
                    ...value,
                    follow_up_concern: {
                      ...(value.follow_up_concern || {}),
                      options: {
                        ...(value.follow_up_concern?.options || {}),
                        hide_from_patient: checked,
                      },
                    },
                  }))
                }
              />
              <DefaultSwitch
                name="follow_up_concern.is_optional"
                label="Optional"
                reversed
                checked={value.follow_up_concern?.options?.is_optional ?? false}
                onChange={(ev, checked) =>
                  setValue((value) => ({
                    ...value,
                    follow_up_concern: {
                      ...(value.follow_up_concern || {}),
                      options: {
                        ...(value.follow_up_concern?.options || {}),
                        is_optional: checked,
                      },
                    },
                  }))
                }
              />
              {value.follow_up_concern?.options?.is_optional && (
                <div>
                  <DefaultTextField
                    name="value.follow_up_concern.icon"
                    label="Icon"
                    style={{ width: '100%' }}
                    value={value.follow_up_concern?.icon}
                    onChange={(event) => {
                      setValue((value) => ({
                        ...value,
                        follow_up_concern: {
                          ...(value.follow_up_concern || {}),
                          icon: event.target.value,
                        },
                      }));
                    }}
                  />
                  {value.follow_up_concern?.icon && (
                    <div
                      style={{
                        fontWeight: 100,
                        color: '#b4b4b4',
                        fontSize: '.75rem',
                        marginTop: '15px',
                      }}
                    >
                      Icon Preview
                      <Icon src={value.follow_up_concern?.icon} width="30px" height="30px" />
                    </div>
                  )}
                  <div
                    style={{
                      fontWeight: 100,
                      color: '#b4b4b4',
                      fontSize: '.75rem',
                      marginTop: '15px',
                    }}
                  >
                    Explanation
                  </div>
                  <TextareaAutosize
                    name="value.follow_up_concern.explanation"
                    label="Explanation"
                    style={{ width: '100%' }}
                    value={value.follow_up_concern?.explanation}
                    onChange={(event) => {
                      setValue((value) => ({
                        ...value,
                        follow_up_concern: {
                          ...(value.follow_up_concern || {}),
                          explanation: event.target.value,
                        },
                      }));
                    }}
                  />
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const RequirementsPicker = ({ requirements, onChange }) => {
  const allRequirements = useAppSelector((state) => Object.values(state.concernRequirements.byId));
  const [showModal, setShowModal] = useState(false);
  const [editingRequirement, setEditingRequirement] = useState(null);

  const handleSelect = (event) => {
    const value = event.target.value;
    if (value === REQUIREMENT_ADD_NEW) {
      setEditingRequirement(null);
      setShowModal(true);
      return;
    }
    if (!requirements.includes(value)) {
      onChange([...requirements, value]);
    }
  };

  const handleRemove = (id) => {
    onChange(requirements.filter((r) => r !== id));
  };

  const handleEdit = (item) => {
    const req = allRequirements.find((r) => r.id === item.id);
    if (!req) return;
    setEditingRequirement(req);
    setShowModal(true);
  };

  // A requirement created from this picker should also be attached to the concern being configured.
  const handleSaved = (saved) => {
    if (!editingRequirement && saved?.id && !requirements.includes(saved.id)) {
      onChange([...requirements, saved.id]);
    }
  };

  return (
    <div className="subsection">
      <h4
        className="subsection-title"
        style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
      >
        Patient Requirements
        <Icon
          src="info-circle"
          width="14px"
          alt="Clearances the patient must have for this concern to appear in their booking flow"
          showTooltip
        />
      </h4>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
        <ConcernRequirementSelector
          label="Add Requirement"
          name="requirements"
          value=""
          onChange={handleSelect}
          excludeIds={requirements}
          extraItems={
            <MenuItem value={REQUIREMENT_ADD_NEW} key="__add_new__">
              <em>+ Create New</em>
            </MenuItem>
          }
        />
      </div>
      {requirements.length > 0 && (
        <StackItemsView
          stack={requirements.map((id) => ({ id }))}
          describeItem={(item) => {
            const req = allRequirements.find((r) => r.id === item.id);
            return req ? <ConcernRequirementBadge requirement={req} /> : item.id;
          }}
          onDeleteItem={(item) => handleRemove(item.id)}
          onEditItem={handleEdit}
          hideStatus
        />
      )}
      <ConcernRequirementModal
        isOpen={showModal}
        requirement={editingRequirement}
        onClose={() => setShowModal(false)}
        onSaved={handleSaved}
      />
    </div>
  );
};

const GrantsClearancesPicker = ({ grantsClearances, onChange }) => {
  const allRequirements = useAppSelector((state) => Object.values(state.concernRequirements.byId));
  const [selectedClearanceId, setSelectedClearanceId] = useState('');
  const [remainingUses, setRemainingUses] = useState('');
  const [expiresAfter, setExpiresAfter] = useState(null);

  const handleAdd = () => {
    if (!selectedClearanceId) return;
    const existing = grantsClearances.find((c) => c.clearance_id === selectedClearanceId);
    if (existing) return;
    const entry = { clearance_id: selectedClearanceId };
    if (remainingUses !== '') {
      entry.remaining_uses = parseInt(remainingUses, 10);
    }
    if (expiresAfter != null) {
      entry.expires_after = expiresAfter;
    }
    onChange([...grantsClearances, entry]);
    setSelectedClearanceId('');
    setRemainingUses('');
    setExpiresAfter(null);
  };

  const handleRemove = (item) => {
    onChange(grantsClearances.filter((c) => c.clearance_id !== item.clearance_id));
  };

  return (
    <div className="subsection">
      <h4
        className="subsection-title"
        style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
      >
        Grants Clearances
        <Icon
          src="info-circle"
          width="14px"
          alt="Clearances granted to the patient when this appointment is completed"
          showTooltip
        />
      </h4>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
        <ConcernRequirementSelector
          label="Clearance"
          name="grants_clearance_id"
          value={selectedClearanceId}
          onChange={(e) => {
            const id = e.target.value;
            setSelectedClearanceId(id);
            const req = allRequirements.find((r) => r.id === id);
            if (req?.default_uses != null) {
              setRemainingUses(String(req.default_uses));
            }
            if (req?.default_expires_after != null) {
              setExpiresAfter(req.default_expires_after);
            }
          }}
          excludeIds={grantsClearances.map((c) => c.clearance_id)}
        />
        <DefaultTextField
          name="remaining_uses"
          label="Uses"
          type="number"
          value={remainingUses}
          onChange={(e) => setRemainingUses(e.target.value)}
          style={{ width: '80px' }}
          placeholder="∞"
        />
        <TimeIntervalField
          label="Expires"
          value={expiresAfter}
          onChange={setExpiresAfter}
          min_interval="days"
          valueWidth="80px"
        />
        <Icon
          src="circle-plus"
          width="20px"
          alt="Add clearance"
          showTooltip
          onClick={handleAdd}
          style={{ cursor: 'pointer' }}
        />
      </div>
      {grantsClearances.length > 0 && (
        <StackItemsView
          stack={grantsClearances}
          itemKey="clearance_id"
          describeItem={(clearance) => {
            const req = allRequirements.find((r) => r.id === clearance.clearance_id);
            const usesText =
              clearance.remaining_uses != null ? `${clearance.remaining_uses} uses` : 'unlimited';
            const expiresText =
              clearance.expires_after != null
                ? `expires after ${clearance.expires_after.value} ${clearance.expires_after.interval}`
                : 'no expiry';
            return (
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                {req ? <ConcernRequirementBadge requirement={req} /> : clearance.clearance_id}
                <span style={{ fontSize: '0.75rem', color: '#888' }}>
                  ({usesText}, {expiresText})
                </span>
              </div>
            );
          }}
          onDeleteItem={handleRemove}
          hideStatus
        />
      )}
    </div>
  );
};

const Skills = ({ concern, onChange }) => {
  return (
    <div className="row">
      <DefaultSelectField
        label="Skills"
        name="skills"
        width="250px"
        value={concern.skills || []}
        onChange={onChange}
        multiple={true}
        inputs={Object.values(PhysicianUtils.PHYSICIAN_SKILLS)}
      />
    </div>
  );
};

const PhysicianTypes = ({ concern, onChange }) => {
  return (
    <div className="row">
      <DefaultSelectField
        label="Practitioner Types"
        name="physician_types"
        width="250px"
        value={concern.physician_types || []}
        onChange={onChange}
        multiple={true}
        inputs={Object.values(PhysicianPhysicianTypeEnum)}
      />
    </div>
  );
};

BookingFlowOptions.propTypes = {};

export default BookingFlowOptions;
